import React from 'react';
import './PageStyles.css';

export default function Privacy() {
  const sections = [
    {
      id: 'overview', title: '1. Overview',
      content: `Ball Sort Master is committed to protecting your privacy. This policy explains what information we collect, how we use it, and your rights regarding your data. By playing Ball Sort Master, you agree to the practices described here. The short version: we collect almost nothing, we store nothing on our servers about your gameplay, and we do not sell or share your data with anyone.`
    },
    {
      id: 'data', title: '2. Data We Collect',
      content: `We collect minimal anonymous data to understand how the game is used and improve the experience. This may include: page views and session duration, device type and screen resolution category, browser type and version, and general geographic region (country-level only). We do NOT collect: your name, email, or any personally identifiable information during normal gameplay, your game state or progress (all game data lives in your browser's local storage and never leaves your device), your move history or puzzle solutions, or any financial or payment information.`
    },
    {
      id: 'gameplay', title: '3. Gameplay Data — Local Only',
      content: `All gameplay data — your current level, score, move history, and undo stack — is stored exclusively in your browser's memory during your session. This data is never transmitted to our servers. When you close the browser tab, this data is cleared. We have no ability to view, access, or record your gameplay activity. You can verify this by opening browser developer tools (F12), navigating to the Network tab, and playing the game — you will observe zero outbound network requests generated by gameplay actions.`
    },
    {
      id: 'cookies', title: '4. Cookies',
      content: `Ball Sort Master uses no advertising cookies, tracking pixels, or cross-site tracking mechanisms. We may use a minimal session cookie for basic analytics (page views). This cookie does not identify you personally and expires at the end of your session. You can disable cookies in your browser settings; the game will continue to function normally without them.`
    },
    {
      id: 'contact-data', title: '5. Contact Form Data',
      content: `If you submit our contact form, we collect your name, email address, selected subject, and message content. This data is used solely to respond to your inquiry. We retain contact submissions for up to 12 months after our last correspondence, then permanently delete them. We never use contact form data for marketing purposes or share it with third parties.`
    },
    {
      id: 'third-party', title: '6. Third-Party Services',
      content: `Ball Sort Master loads web fonts from Google Fonts (fonts.googleapis.com). Google may log font requests per their own privacy policy. No other third-party scripts, analytics platforms, advertising networks, or tracking services are loaded by this game. All game logic, animations, sound effects, and visual rendering run entirely within your browser using standard web APIs.`
    },
    {
      id: 'rights', title: '7. Your Rights',
      content: `You have the right to access any personal data we hold about you (limited to contact form submissions), request deletion of your personal data, and object to processing. To exercise any of these rights, use our Contact page. We will respond within 30 days. Users in the European Economic Area have additional rights under GDPR, including the right to lodge a complaint with your local data protection authority.`
    },
    {
      id: 'children', title: '8. Children\'s Privacy',
      content: `Ball Sort Master is a general-audience puzzle game suitable for all ages. We do not knowingly collect personal data from children under 13. The game does not require account creation, age verification, or any personal information submission to play. If you believe a child has submitted personal data via our contact form, please contact us immediately and we will delete it promptly.`
    },
    {
      id: 'changes', title: '9. Changes to This Policy',
      content: `We may update this Privacy Policy to reflect changes in our practices or applicable law. When we make significant changes, we will update the date below. Continued use of Ball Sort Master after an update constitutes acceptance of the revised policy. We encourage you to review this page periodically.`
    },
    {
      id: 'contact-us', title: '10. Contact Us',
      content: `For privacy questions, data requests, or concerns about this policy, please use our Contact page. We take privacy inquiries seriously and respond to all requests promptly.`
    },
  ];

  return (
    <main className="inner-page">
      <div className="page-hero">
        <div className="container">
          <div className="page-badge">Legal</div>
          <h1>Privacy <span className="accent-glow">Policy</span></h1>
          <p>Last updated: January 1, 2025 — We believe in full transparency about how we handle your data.</p>
        </div>
      </div>

      <div className="container page-content">
        <div className="privacy-layout">
          {/* Mobile TOC pills */}
          <div className="toc-pills">
            {sections.map(s => (
              <a key={s.id} href={`#${s.id}`} className="toc-pill">{s.title.split('.')[0]}. {s.title.split(' ').slice(1).join(' ')}</a>
            ))}
          </div>

          {/* Desktop sidebar TOC */}
          <aside className="privacy-toc glass-card">
            <h4>Contents</h4>
            <ul>
              {sections.map(s => (
                <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>
              ))}
            </ul>
          </aside>

          <div className="privacy-content">
            <div className="glass-card content-card">
              <div className="highlight-box">
                <strong>🔒 The Short Version:</strong> We do not log your gameplay. All game data stays in your browser. We collect only anonymous analytics. No ads, no tracking pixels, no data sales.
              </div>
              {sections.map(s => (
                <section key={s.id} id={s.id} className="policy-section">
                  <h2>{s.title}</h2>
                  <p>{s.content}</p>
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
